import React, {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTheme } from "@mui/material/styles";

import CameraScan from "components/CameraScan/CameraScan";
import MDButton from "components/MDButton";
import CellPhoneTest from "assets/images/step3.svg?react";
import { useTranslation } from "react-i18next";
import { LoaderModelModal } from "components/LoaderModelModal/LoaderModelModal";
import { useAppDispatch, useAppSelector } from "store/hooks";
import configuredStore from "store/store";
import { getSessionId, getTestTypeValue } from "store/slices/test";
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { endCameraSession, startCameraSession } from "services/sentryTiming";
import { useStyles } from "./Step3.styles";
import { CameraDeniedModal } from "components/CameraDeniedModal/CameraDeniedModal";
import { checkCameraPermission } from "utils/camera.util";
import { sentryLogException } from "services/sentryLogger";

import { processImage, resetImageProcessingSession, getModelMetaDataTags } from "services/models";
import { setShouldStopImageProcess } from "store/slices/imageRecognition";
interface StepProps {
  handleUpdateStep: (step: number) => void;
}

const Step3: FC<StepProps> = ({ handleUpdateStep }) => {
  const dispatch = useAppDispatch();
  const [showCamera, setShowCamera] = React.useState(false);
  const shouldActiveFiltering = useRef(true);
  const cameraSessionStarted = useRef(false);
  const [permisionDeniedVissible, setPermisionDeniedVissible] = useState(false);
  const [screenOrientation, setScreenOrientation] =
    useState("portrait-primary");
  const msg = useAppSelector((state) => state.imageRecognition.msg);
  const status = useAppSelector((state) => state.imageRecognition.status);
  const result = useAppSelector((state) => state.imageRecognition.result);
  const isModelsLoaded = useAppSelector(
    (state) => state.imageRecognition.isModelsLoaded
  );

  const shouldStopImageProcess = useAppSelector(
    (state) => state.imageRecognition.shouldStopImageProcess
  );

  const theme = useTheme();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  const sessionId = useAppSelector(getSessionId);

  const openCamera = useCallback(async () => {
    try {
      await checkCameraPermission();
      resetImageProcessingSession();
      dispatch(setShouldStopImageProcess(false));
      setPermisionDeniedVissible(false);
      
      // Start camera Sentry session timing
      startCameraSession(sessionId);
      cameraSessionStarted.current = true;
      
      setShowCamera(true);
    } catch {
      setPermisionDeniedVissible(true);
    }
  }, [dispatch]);

  const closeCamera = useCallback(() => {
    // Read the current result from Redux directly to avoid stale closure issues
    const currentResult = configuredStore.getState().imageRecognition.result;
    // Don't close camera if REJECTED - user needs to retry
    if (currentResult === "REJECTED") {
      return;
    }
    if (cameraSessionStarted.current) {
      endCameraSession(0);
      cameraSessionStarted.current = false;
    }
    dispatch(setShouldStopImageProcess(true));
    resetImageProcessingSession();
    setShowCamera(false);
  }, [dispatch]);

  const setStyle = () => {
    const orientationType =
      document.body.scrollHeight < document.body.scrollWidth
        ? "landscape-primary"
        : "portrait-primary";

    setScreenOrientation(orientationType);
  };

  const captureImage = useCallback(async (data: any) => {
    const processImageData = {
      data: data.img,
      index: data.index,
      shouldActiveFiltering: shouldActiveFiltering,
    };
    try {
      if (!shouldStopImageProcess && !result) {
        await processImage(processImageData);
      } else {
        console.log("Image process stopped");
      }
    } catch (error) {
      console.error("processImage ERROR: ", error);
      sentryLogException({
        error,
        tags: {
          component: 'Step3',
          category: 'processImage',
          testType,
          ...getModelMetaDataTags(),
        },
        extra: {
          sessionId,
          imageIndex: processImageData.index,
          timestamp: new Date().toISOString(),
        },
      });
    }
    // worker.processImage(processImageData, imageStack);
  }, [shouldStopImageProcess, result]);

  useEffect(() => {
    setStyle();
    function handleResize() {
      setStyle();
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (result === "INVALID") {
      handleUpdateStep(6); // Go directly to ModalsActions
    } else if (result === "POSITIVE" || result === "NEGATIVE") {
      handleUpdateStep(5); // Go to TestResults (POSITIVE or NEGATIVE)
    }
    // Do NOT close camera or navigate on REJECTED - keep camera open for retry
  }, [result, handleUpdateStep]);

  useEffect(() => {
    if (!showCamera) {
      return;
    }

    const isDefinitiveResult =
      result === "POSITIVE" || result === "NEGATIVE" || result === "INVALID";

    if (isDefinitiveResult) {
      setShowCamera(false);
    }
  }, [showCamera, result]);

  useEffect(() => {
    return () => {
      if (cameraSessionStarted.current) {
        endCameraSession(0);
        cameraSessionStarted.current = false;
      }
      resetImageProcessingSession();
    };
  }, []);

  if (permisionDeniedVissible) {
    return (
      <CameraDeniedModal tryAgain={() => setPermisionDeniedVissible(false)} />
    );
  }

  return (
    <div className={classes.container}>
      <LoaderModelModal
        modelLoaded={isModelsLoaded}
        title={t(`${testTypeLocaleKey}:step3screen:modalTitle`)}
        description={t(`${testTypeLocaleKey}:step3screen:pleaseImproveConnection`)}
      />
      <div
        hidden={screenOrientation === "landscape-primary"}
        className={classes.bodyContainer}
      >
        <div className={classes.bodyTitle}>{t(`${testTypeLocaleKey}:step3screen:bodyTitle`)}</div>
        <div className={classes.bodyText}>{t(`${testTypeLocaleKey}:step3screen:bodyText`)}</div>
        <div className={classes.bodyImage}>
          <CellPhoneTest />
        </div>
      </div>
      <MDButton
        style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
        color={"primary"}
        variant="contained"
        type="submit"
        onClick={openCamera}
        disabled={!isModelsLoaded}
      >
        {t(`${testTypeLocaleKey}:step3screen:captureButton`)}
      </MDButton>
      {showCamera ? (
        <CameraScan
          status={status}
          message={msg}
          captureEnabled={!shouldStopImageProcess && !result}
          onImageCapture={captureImage}
          onClose={closeCamera}
          showNotSureOverlay={result === "REJECTED"}
          notSureMessage={`${testTypeLocaleKey}:step3screen:rejectMessage`}
        />
      ) : null}
    </div>
  );
};

export default Step3;
